import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Wallet, AlertTriangle, Clock, TrendingUp, ArrowRight, ChevronRight, FileText, Layers, PenTool, ShoppingBag, RefreshCw } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { MonthlyLedgerChart, RecentActivity } from '../../components/dashboard/DashboardWidgets';
import {
  getBrandProfile,
  getAllEngagements,
  getAllFabricOptions,
  getAllDesigns,
  getAllOrders,
  getAllPaymentReceipts,
} from '../../store/actions';
import { ENGAGEMENT_STAGE, formatINR } from '../../constants/garmentCodes';

const FABRIC_SUBMITTED = 2;
const DESIGN_AWAITING_CHARGE = 1;
const ORDER_ESTIMATE_PENDING = 3;
const RECEIPT_SUBMITTED = 2;
const STAGE_COUNT = 15;
const isLow = (eng) => eng.lowBalanceThreshold != null && (eng.availableBalance ?? 0) < eng.lowBalanceThreshold;

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

function KPICard({ label, value, sub, variant = 'default', icon: Icon, to, warning }) {
  const colors = {
    green: 'bg-[#E8F5EE] border-[#A8DEC0] text-[#1A6B3E]',
    amber: 'bg-[#FEF3E2] border-[#F7C97A] text-[#92560C]',
    red: 'bg-[#FDECEA] border-[#F5A9AC] text-[#C0222B]',
    indigo: 'bg-[#EEF0FB] border-[#B8C1E8] text-[#3D52A0]',
    default: 'bg-white border-[#E5E3DE] text-[#1A1915]',
  };
  return (
    <Link to={to}>
      <div className={`relative rounded-xl border p-4 hover:shadow-sm transition-shadow cursor-pointer group ${colors[variant] || colors.default}`}>
        {warning && <AlertTriangle size={12} className="absolute top-3 right-3 text-[#92560C]" />}
        <div className="flex items-start gap-3">
          {Icon && <div className="p-2 rounded-lg bg-white/60 shrink-0"><Icon size={14} /></div>}
          <div className="min-w-0">
            <p className="text-[10px] font-medium text-[#9B9893] uppercase tracking-wider">{label}</p>
            <p className="text-xl font-bold mt-0.5">{value}</p>
            {sub && <p className="text-[11px] text-[#9B9893] mt-0.5">{sub}</p>}
          </div>
        </div>
        <ChevronRight size={12} className="absolute bottom-3 right-3 text-[#9B9893] opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </Link>
  );
}

function DecisionRow({ icon: Icon, color, title, sub, to }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E3DE] hover:border-[#B8C1E8] hover:bg-[#FAFAF8] transition-all group">
      <div className="p-2 rounded-lg bg-[#F4F3F0] shrink-0"><Icon size={14} className={color} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#1A1915] truncate">{title}</p>
        <p className="text-xs text-[#9B9893] mt-0.5">{sub}</p>
      </div>
      <Badge variant="amber">Action needed</Badge>
      <ChevronRight size={14} className="text-[#9B9893] opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
}

export default function BrandDashboardIndex() {
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state.auth?.loginUser);
  const profile = useSelector((state) => state.brand.profile);
  const brandId = profile?.id;
  const dashboard = useSelector((state) => state.brand.dashboard);
  const engagements = useSelector((state) => state.engagement.engagements);
  const fabrics = useSelector((state) => state.fabricoption.options);
  const designs = useSelector((state) => state.design.designs);
  const estimates = useSelector((state) => state.order.orders);
  const receipts = useSelector((state) => state.paymentreceipt.receipts);

  useEffect(() => {
    dispatch(getBrandProfile({ withDashboard: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllEngagements({ brandId }));
    dispatch(getAllFabricOptions({ brandId, decisionStatus: FABRIC_SUBMITTED }));
    dispatch(getAllDesigns({ brandId, status: DESIGN_AWAITING_CHARGE }));
    dispatch(getAllOrders({ brandId, status: ORDER_ESTIMATE_PENDING }));
    dispatch(getAllPaymentReceipts({ brandId, status: RECEIPT_SUBMITTED }));
  }, [dispatch, brandId]);

  const decisions = fabrics.length + designs.length + estimates.length;
  const lowEngagements = engagements.reduce((acc, e) => (isLow(e) ? [...acc, e] : acc), []);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title={`${greeting()}${loginUser?.name ? `, ${loginUser.name}` : ''}`} subtitle={profile?.name || ''} />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {!profile && (
          <div className="flex items-center gap-3 p-4 bg-[#EEF0FB] border border-[#B8C1E8] rounded-xl text-sm text-[#3D52A0]">
            Your brand profile is not set up yet.
            <Link to="/brand/settings" className="font-semibold hover:underline">Set up profile →</Link>
          </div>
        )}

        {lowEngagements.map((eng) => (
          <div key={eng.id} className="flex items-center gap-3 p-4 bg-[#FEF3E2] border border-[#F7C97A] rounded-xl">
            <AlertTriangle size={16} className="text-[#92560C] shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#92560C]">Low balance — {eng.manufacturerName}</p>
              <p className="text-xs text-[#92560C]/80">{formatINR(eng.availableBalance)} available — threshold is {formatINR(eng.lowBalanceThreshold)}. Submit a payment receipt to top up.</p>
            </div>
            <Link to={`/brand/receipts?engagementId=${eng.id}`}><Button variant="secondary" size="sm">Submit receipt</Button></Link>
          </div>
        ))}

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <KPICard label="Current Balance" value={formatINR(dashboard?.walletBalance)} sub="across engagements" icon={Wallet} to="/brand/wallet" variant="indigo" />
          <KPICard label="Available Balance" value={formatINR(dashboard?.availableBalance)} sub="gates new work" icon={TrendingUp} to="/brand/wallet" variant={lowEngagements.length ? 'amber' : 'green'} warning={lowEngagements.length > 0} />
          <KPICard label="Held / Committed" value={formatINR(dashboard?.heldAmount)} sub="approved, not billed" icon={Clock} to="/brand/wallet" variant="amber" />
          <KPICard label="Receipts Under Review" value={receipts.length} sub="awaiting manufacturer" icon={RefreshCw} to="/brand/receipts" />
          <KPICard label="Pending Decisions" value={decisions} sub="need your action" icon={AlertTriangle} to="/brand/board" variant={decisions > 0 ? 'red' : 'green'} warning={decisions > 0} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard label="Fabric Decisions" value={fabrics.length} sub="options awaiting review" icon={Layers} to="/brand/fabrics" />
          <KPICard label="Design Charges" value={designs.length} sub="awaiting approval" icon={PenTool} to="/brand/designs" />
          <KPICard label="Orders" value={dashboard?.orderCount ?? '—'} sub={`${estimates.length} estimate(s) to review`} icon={ShoppingBag} to="/brand/orders" />
          <KPICard label="Requirements" value={dashboard?.requirementCount ?? '—'} sub={`across ${dashboard?.partnerCount ?? 0} manufacturer(s)`} icon={FileText} to="/brand/requirements" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1 bg-white rounded-xl border border-[#E5E3DE] overflow-hidden">
            <div className="p-5 border-b border-[#E5E3DE]"><h2 className="text-sm font-semibold text-[#1A1915]">Engagement Wallets</h2></div>
            <div className="p-5 space-y-4">
              {engagements.length === 0 && <p className="text-sm text-[#9B9893] text-center">No engagements yet</p>}
              {engagements.map((eng) => (
                <Link key={eng.id} to={`/brand/wallet?engagementId=${eng.id}`} className="block">
                  <p className="text-xs font-medium text-[#1A1915]">{eng.manufacturerName}</p>
                  <div className="grid grid-cols-3 gap-2 mt-1.5 text-center">
                    <div className="bg-[#EEF0FB] rounded-lg p-2"><p className="text-[9px] text-[#3D52A0] uppercase">Balance</p><p className="text-xs font-bold text-[#3D52A0]">{formatINR(eng.walletBalance)}</p></div>
                    <div className="bg-[#FEF3E2] rounded-lg p-2"><p className="text-[9px] text-[#92560C] uppercase">Held</p><p className="text-xs font-bold text-[#92560C]">{formatINR(eng.heldAmount)}</p></div>
                    <div className="bg-[#E8F5EE] rounded-lg p-2"><p className="text-[9px] text-[#1A6B3E] uppercase">Available</p><p className="text-xs font-bold text-[#1A6B3E]">{formatINR(eng.availableBalance)}</p></div>
                  </div>
                </Link>
              ))}
              <div className="flex gap-2 pt-2 border-t border-[#E5E3DE]">
                <Link to="/brand/wallet" className="flex-1"><Button variant="outline" size="sm" className="w-full">View ledger</Button></Link>
                <Link to="/brand/receipts" className="flex-1"><Button variant="primary" size="sm" className="w-full">Submit receipt</Button></Link>
              </div>
            </div>
          </div>

          <div className="xl:col-span-2 bg-white rounded-xl border border-[#E5E3DE] overflow-hidden">
            <div className="p-5 border-b border-[#E5E3DE]">
              <h2 className="text-sm font-semibold text-[#1A1915]">Decision Centre</h2>
              <p className="text-xs text-[#9B9893]">{decisions} items need your attention</p>
            </div>
            <div className="p-5 space-y-2">
              {decisions === 0 && <p className="text-sm text-[#9B9893] text-center py-4">You're all caught up</p>}
              {fabrics.map((f) => <DecisionRow key={`f-${f.id}`} icon={Layers} color="text-[#0C6E6E]" title={`Fabric option — ${f.name}`} sub={[f.requirementName, f.manufacturerName].filter(Boolean).join(' · ')} to="/brand/fabrics" />)}
              {designs.map((d) => <DecisionRow key={`d-${d.id}`} icon={PenTool} color="text-[#3D52A0]" title={`Design charge — ${d.name}`} sub={`${formatINR(d.charge)} · ${d.manufacturerName || ''}`} to="/brand/designs" />)}
              {estimates.map((o) => <DecisionRow key={`o-${o.id}`} icon={ShoppingBag} color="text-[#92560C]" title={`Cost estimate — ${o.orderNumber || o.product}`} sub={`${formatINR(o.totalEstimate)} · ${o.product || ''}`} to="/brand/orders" />)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden">
            <div className="p-5 border-b border-[#E5E3DE] flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[#1A1915]">Engagement Progress</h2>
              <Link to="/brand/engagements"><Button variant="ghost" size="sm" iconRight={<ArrowRight size={12} />}>All engagements</Button></Link>
            </div>
            <div className="p-5 space-y-4">
              {engagements.length === 0 && <p className="text-sm text-[#9B9893] text-center">No engagements yet</p>}
              {engagements.map((eng) => (
                <div key={eng.id}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-medium text-[#1A1915]">{eng.name}</span>
                    <Badge variant="indigo">{ENGAGEMENT_STAGE[eng.stage] || '—'}</Badge>
                  </div>
                  <div className="h-1.5 bg-[#F4F3F0] rounded-full overflow-hidden">
                    <div className="h-full bg-[#3D52A0] rounded-full" style={{ width: `${Math.min(100, ((eng.stage || 0) / STAGE_COUNT) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <RecentActivity items={dashboard?.recentActivity} basePath="/brand" />
        </div>

        <MonthlyLedgerChart months={dashboard?.monthlyLedger} />
      </div>
    </div>
  );
}
